import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';
import { articles, categories } from '@/mocks/data';

export default function Article() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/category/all" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
              返回资源中心
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categories.find((c) => c.slug === article.category)?.name || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '91牛',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://91niu.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '资源中心',
        item: 'https://91niu.com/category/all',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: catName,
        item: `https://91niu.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOSchema
        title={`${article.title} - 91牛平台`}
        description={article.excerpt}
        keywords={`91牛,91牛平台,${article.title},${catName}`}
        canonicalUrl={`https://91niu.com/article/${article.id}`}
        ogType="article"
        schema={[articleSchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-6 md:px-12 py-10">
          <div className="mb-8">
            <Link
              to={`/category/${article.category}`}
              className="inline-block whitespace-nowrap bg-primary-100 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 hover:bg-primary-200 transition-colors cursor-pointer"
            >
              {catName}
            </Link>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {article.readTime}
              </span>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to={`/category/${article.category}`}
                className="whitespace-nowrap inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                返回{catName}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500">分享到：</span>
                <a
                  href="#"
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                  rel="nofollow"
                >
                  <i className="ri-wechat-line" />
                </a>
                <a
                  href="#"
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                  rel="nofollow"
                >
                  <i className="ri-weibo-line" />
                </a>
              </div>
            </div>
          </div>

          <section className="mt-10">
            <h3 className="font-heading text-lg text-foreground-900 mb-5">相关文章</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {articles
                .filter((a) => a.id !== article.id && a.category === article.category)
                .slice(0, 2)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group flex items-start gap-4 p-4 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <img
                      src={related.coverImage}
                      alt={related.title}
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{related.date}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}